import React, { useContext } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { motion } from 'framer-motion';
import L from 'leaflet';
import {
  FaClock,
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTiktok,
  FaWhatsapp,
} from 'react-icons/fa';
import { LanguageContext } from '../../../contexts/LanguageContext';
import {
  RESTAURANT_ADDRESS,
  RESTAURANT_CITY,
  RESTAURANT_EMAIL,
  RESTAURANT_MAPS_URL,
  RESTAURANT_NAME,
  RESTAURANT_PHONE,
  RESTAURANT_PHONE_DISPLAY,
  RESTAURANT_WHATSAPP_DISPLAY,
  RESTAURANT_WHATSAPP_URL,
} from '../../../constants/contact';
import { openReservationModal } from '../../../utils/openReservationModal';
import '../css/LocationPage.css';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const position = [41.3771156, 2.1370757];

const socialLinks = [
  {
    key: 'instagram',
    href: 'https://instagram.com/the_vatti',
    icon: FaInstagram,
  },
  {
    key: 'facebook',
    href: 'https://www.facebook.com/share/1ANQV4cTYB/',
    icon: FaFacebook,
  },
  {
    key: 'tiktok',
    href: 'https://tiktok.com/@the.vatti',
    icon: FaTiktok,
  },
];

const LocationPage = () => {
  const { t } = useContext(LanguageContext);

  const contactMethods = [
    {
      key: 'phone',
      icon: FaPhoneAlt,
      label: t('phone'),
      value: RESTAURANT_PHONE_DISPLAY,
      href: `tel:${RESTAURANT_PHONE}`,
    },
    {
      key: 'whatsapp',
      icon: FaWhatsapp,
      label: t('whatsapp'),
      value: RESTAURANT_WHATSAPP_DISPLAY,
      href: RESTAURANT_WHATSAPP_URL,
    },
    {
      key: 'email',
      icon: FaEnvelope,
      label: t('email'),
      value: RESTAURANT_EMAIL,
      href: `mailto:${RESTAURANT_EMAIL}`,
    },
  ];

  return (
    <motion.section
      className="location-page"
      whileInView={{ opacity: 1 }}
      viewport={{ once: false, amount: 0.2 }}
      transition={{ duration: 0.6 }}
    >
      <div className="location-container">
        <span className="section-kicker">{t('visitUs')}</span>
        <h2 className="section-title location-title">{t('location')}</h2>
        <p className="section-description">{t('locationLead')}</p>

        <div className="location-content">
          <motion.div
            className="location-copy"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <div className="location-intro">
              <div className="location-address-block">
                <span className="location-eyebrow">{RESTAURANT_NAME}</span>
                <h3>{RESTAURANT_ADDRESS}</h3>
                <p>{RESTAURANT_CITY}</p>
              </div>

              <div className="location-detail-row">
                <FaClock aria-hidden="true" />
                <div>
                  <strong>{t('openingHours')}</strong>
                  <p>{t('serviceMoments')}</p>
                </div>
              </div>

              <div className="location-detail-row">
                <FaMapMarkerAlt aria-hidden="true" />
                <div>
                  <strong>{t('visitUs')}</strong>
                  <p>{t('locationLead')}</p>
                </div>
              </div>
            </div>

            <div className="location-actions">
              <a
                href={RESTAURANT_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="open-google-maps-btn"
              >
                {t('openMaps')}
              </a>

              <button type="button" className="reserve-action-btn" onClick={openReservationModal}>
                {t('reserveTable')}
              </button>
            </div>

            <div className="contact-rail" aria-label={t('contactUs')}>
              {contactMethods.map(({ key, icon: Icon, label, value, href }) => (
                <a
                  key={key}
                  className="contact-line"
                  href={href}
                  target={key === 'whatsapp' ? '_blank' : undefined}
                  rel={key === 'whatsapp' ? 'noopener noreferrer' : undefined}
                >
                  <span className="contact-line-icon">
                    <Icon aria-hidden="true" />
                  </span>

                  <span className="contact-line-label">{label}</span>
                  <strong className="contact-line-value">{value}</strong>
                </a>
              ))}
            </div>

            <div className="social-strip">
              <span className="social-strip-label">{t('followUs')}</span>

              <div className="social-links">
                {socialLinks.map(({ key, href, icon: Icon }, index) => (
                  <motion.a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label={key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                  >
                    <Icon size={22} />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="map-wrapper"
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.8 }}
          >
            <MapContainer center={position} zoom={17} className="location-map">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={position}>
                <Popup>
                  {RESTAURANT_NAME}
                  <br />
                  {RESTAURANT_ADDRESS}
                  <br />
                  {RESTAURANT_CITY}
                </Popup>
              </Marker>
            </MapContainer>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default LocationPage;
